import { Link, Navigate } from "react-router-dom";
import { ArrowRight, Check, Info } from "lucide-react";
import { Seo } from "@/components/Seo";
import { DeviceImage } from "@/components/DeviceFrames";
import { QuoteForm } from "@/components/QuoteForm";
import { SERVICES, SERVICE_OPTION_BY_SLUG } from "@/data/content";

const SERVICE_IMAGES = {
  "creation-site-web": {
    layout: "wide",
    items: [
      {
        type: "photo",
        src: "/mockups/concept-vitrine.webp",
        alt: "Concept de site vitrine pour une entreprise de services",
        caption: "Site vitrine — concept de démonstration",
      },
      {
        type: "photo",
        src: "/mockups/concept-restaurant.webp",
        alt: "Concept de site élégant pour un restaurant",
        caption: "Site restaurant — concept de démonstration",
      },
      {
        type: "photo",
        src: "/mockups/concept-ecommerce.webp",
        alt: "Concept de boutique en ligne de mode",
        caption: "Boutique e-commerce — concept de démonstration",
      },
      {
        type: "photo",
        src: "/mockups/concept-conseil.webp",
        alt: "Concept de site professionnel pour un cabinet de conseil",
        caption: "Site cabinet de conseil — concept de démonstration",
      },
    ],
  },
  "applications-mobiles": {
    layout: "narrow",
    items: [
      {
        type: "photo",
        src: "/mockups/concept-app-reservation.webp",
        alt: "Concept d'application mobile de réservation avec calendrier",
        caption: "App de réservation — concept de démonstration",
      },
      {
        type: "photo",
        src: "/mockups/concept-app-commerce.webp",
        alt: "Concept d'application mobile de commerce avec panier",
        caption: "App de commerce — concept de démonstration",
      },
    ],
  },
  "referencement-seo": {
    layout: "wide",
    items: [
      {
        type: "photo",
        src: "/mockups/concept-seo.webp",
        alt: "Tableau de bord SEO Koranex : santé du site, trafic organique, mots-clés et audit technique — illustration de démonstration",
        caption: "Suivi et rapports SEO — illustration de démonstration",
      },
    ],
  },
  "maintenance-site-web": {
    layout: "wide",
    items: [
      {
        type: "browser",
        src: "https://images.unsplash.com/photo-1644088379091-d574269d422f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwxfHx0ZWNobm9sb2d5JTIwbmV0d29yayUyMGN5YW58ZW58MHx8fHwxNzkyMjUxNzUxfDA&ixlib=rb-4.1.0&q=85",
        alt: "Illustration de surveillance et de maintenance d'un site web — démonstration",
        caption: "Surveillance continue — illustration de démonstration",
      },
    ],
  },
};

export default function ServicePage({ slug }) {
  const service = SERVICES.find((s) => s.slug === slug);
  if (!service) return <Navigate to="/" replace />;

  const visualSet = SERVICE_IMAGES[slug];
  const images = visualSet ? visualSet.items : [];
  const narrowGrid = visualSet?.layout === "narrow";

  return (
    <div data-testid={`service-page-${slug}`}>
      <Seo title={service.seo.title} description={service.seo.description} />

      {/* HERO */}
      <header className="py-16 md:py-24 px-6 max-w-6xl mx-auto">
        <span className="text-xs font-mono-x uppercase text-[#38bdf8] tracking-widest kx-up inline-block">
          {service.kicker}
        </span>
        <h1 className="font-display text-4xl sm:text-6xl md:text-7xl text-white leading-none mt-3 mb-6 kx-up delay-100">
          {service.heroTitle}
        </h1>
        <p className="text-base sm:text-lg text-slate-300 max-w-3xl leading-relaxed mb-10 kx-up delay-200">
          {service.heroText}
        </p>
        <div className="flex flex-wrap gap-4 kx-up delay-300">
          <a
            href="#devis"
            data-testid="service-hero-quote-button"
            className="px-8 py-4 rounded-xl bg-[#1d4ed8] hover:bg-blue-600 text-white font-mono-x text-xs uppercase tracking-wider font-bold transition-colors shadow-xl shadow-blue-950 kx-glow"
          >
            {slug === "applications-mobiles" ? "Demander un devis pour mon application" : "Demander un devis gratuit"}
          </a>
          <Link
            to="/realisations"
            data-testid="service-hero-portfolio-button"
            className="px-8 py-4 rounded-xl bg-[#0f172a] hover:bg-slate-800 border border-slate-800 text-slate-300 font-mono-x text-xs uppercase tracking-wider transition-colors"
          >
            Voir nos réalisations
          </Link>
        </div>
      </header>

      {/* OFFRES */}
      <section className="py-14 md:py-20 bg-[#050b18] border-t border-slate-800 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-display text-3xl sm:text-5xl text-white mb-12">Ce que nous réalisons</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {service.offerings.map((o, i) => (
              <div
                key={o.title}
                data-testid={`offering-${slug}-${i}`}
                className="p-6 rounded-2xl bg-[#0f172a] border border-slate-800 hover:border-[#1d4ed8] transition-colors"
              >
                <span className="text-xs font-mono-x text-[#38bdf8]">{String(i + 1).padStart(2, "0")}</span>
                <h3 className="font-bold text-white text-base mt-2 mb-2">{o.title}</h3>
                <p className="text-xs text-slate-400 leading-relaxed">{o.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* VISUELS */}
      {images.length > 0 && (
        <section className="py-14 md:py-20 px-6 max-w-6xl mx-auto">
          <h2 className="font-display text-3xl sm:text-5xl text-white mb-4">Aperçus visuels</h2>
          <p className="text-xs font-mono-x text-slate-500 mb-12">
            Concepts de démonstration — ils seront remplacés par les réalisations réelles de nos clients.
          </p>
          <div
            className={`grid gap-8 items-start ${
              narrowGrid ? "grid-cols-1 sm:grid-cols-2 max-w-2xl mx-auto" : "grid-cols-1 md:grid-cols-2"
            }`}
          >
            {images.map((img, i) => (
              <figure key={i} data-testid={`service-visual-${slug}-${i}`} className={images.length === 1 ? "md:col-span-2 max-w-3xl mx-auto w-full" : ""}>
                <DeviceImage device={img.type} src={img.src} alt={img.alt} />
                <figcaption className="text-[10px] font-mono-x uppercase tracking-widest text-slate-500 mt-3 text-center">
                  {img.caption}
                </figcaption>
              </figure>
            ))}
          </div>
        </section>
      )}

      {/* ÉTAPES */}
      <section className="py-14 md:py-20 bg-[#050b18] border-t border-slate-800 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-display text-3xl sm:text-5xl text-white mb-12">Comment se déroule votre projet</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {service.steps.map((step, i) => (
              <div key={step.title} className="p-6 rounded-xl bg-[#0f172a] border border-slate-800">
                <span className="text-2xl font-display text-[#1d4ed8]">{String(i + 1).padStart(2, "0")}</span>
                <h3 className="font-bold text-white text-base mt-2 mb-1">{step.title}</h3>
                <p className="text-xs text-slate-400 leading-relaxed">{step.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* LIVRABLES */}
      <section className="py-14 md:py-20 px-6 max-w-6xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          <div>
            <h2 className="font-display text-3xl sm:text-5xl text-white mb-8">Ce qui est livré</h2>
            <ul className="space-y-3">
              {service.deliverables.map((d) => (
                <li key={d} className="flex items-start gap-3 text-sm text-slate-300">
                  <Check className="w-4 h-4 text-[#38bdf8] mt-0.5 shrink-0" />
                  {d}
                </li>
              ))}
            </ul>
          </div>
          {service.note && (
            <div className="rounded-2xl border border-[#38bdf8]/30 bg-[#0f172a] p-6 flex gap-4">
              <Info className="w-5 h-5 text-[#38bdf8] shrink-0 mt-0.5" />
              <p className="text-sm text-slate-300 leading-relaxed">{service.note}</p>
            </div>
          )}
        </div>
      </section>

      {/* FORMULAIRE */}
      <section id="devis" className="py-14 md:py-20 bg-[#050b18] border-t border-slate-800 px-6 scroll-mt-20">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-10">
            <span className="text-xs font-mono-x uppercase text-[#38bdf8] tracking-widest">Devis gratuit</span>
            <h2 className="font-display text-3xl sm:text-5xl text-white mt-1 mb-3">Lançons votre projet</h2>
            <p className="text-xs font-mono-x text-slate-400">
              Décrivez votre besoin, nous revenons vers vous avec un devis gratuit et sans engagement.
            </p>
          </div>
          <QuoteForm formId={`${slug}-quote`} defaultService={SERVICE_OPTION_BY_SLUG[slug] || ""} />
        </div>
      </section>

      <div className="py-12 px-6 text-center">
        <Link
          to="/realisations"
          data-testid="service-bottom-portfolio-link"
          className="inline-flex items-center gap-2 text-xs font-mono-x font-bold uppercase tracking-wider text-[#38bdf8] hover:text-white transition-colors"
        >
          Explorer la galerie de réalisations <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      </div>
    </div>
  );
}
